import type { LineupRow } from '../types'
import { SectionLabel } from './RoundTimeline'
import { fmtDate } from '../data/fmt'

// Which five actually played each demo, measured against the most recent one.
//
// A folder — or a season — is a set of games, not a team. Seven demos of "them" can be three
// different fives, and a season that loses a player halfway is two teams' numbers in one table.
// Every aggregate above this blends them into one confident-looking figure. Same failure as folding
// a renamed player into two half-rows: nothing on screen is wrong, and the total means less than it
// looks like it does.
//
// Shared by Scouting and Season deliberately: the question "whose numbers are these" is the same
// one pointed in two directions, and it would drift if it were written twice.

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

export function Lineups({ rows, who }: { rows: LineupRow[]; who: 'us' | 'them' }) {
  // Newest first — the current five is the yardstick everything else is measured against.
  const ordered = [...rows].reverse()
  const mixed = rows.filter((l) => l.overlap <= 3)
  const ours = who === 'us'

  return (
    <section
      className="border p-5"
      style={{ ...card, borderColor: mixed.length ? 'var(--loss)' : 'var(--border)' }}
    >
      <SectionLabel n={ours ? 'Y1b' : 'S4b'} label="Whose numbers are these?" />
      {mixed.length > 0 ? (
        <p className="mt-2 text-xs leading-relaxed text-loss">
          {mixed.length} of {rows.length} {ours ? 'matches' : 'demos'} share{' '}
          {Math.min(...mixed.map((l) => l.overlap))}–3 players with{' '}
          {ours ? 'our current five' : 'their latest five'}. Everything else on this page blends
          those rosters —{' '}
          {ours
            ? 'a per-map record from before a roster change is not this team’s record.'
            : 'read the per-map and per-player numbers as "this org", not "this team".'}
        </p>
      ) : (
        <p className="mt-2 text-xs leading-relaxed text-text-faint">
          Same five in every {ours ? 'match' : 'demo'} — everything on this page is about one team.
        </p>
      )}
      <div className="mt-3 space-y-1">
        {ordered.map((l, i) => (
          <div key={l.demoId} className="flex items-center gap-3 text-[11px]">
            <span className="w-16 shrink-0 font-display uppercase tracking-wider text-text-dim">
              {l.map.replace('de_', '')}
            </span>
            <span className="w-20 shrink-0 text-text-faint">{l.at ? fmtDate(l.at) : '—'}</span>
            {/* Five pips: how much of that game's five is still in the team today. Colour alone
                can't carry that, so the row states it in words for a screen reader. */}
            <span
              className="flex shrink-0 gap-0.5"
              title={l.names.join(', ')}
              role="img"
              aria-label={`${l.overlap} of ${l.sids.length} still in the current lineup`}
            >
              {/* One pip per player who actually played it. A match with a sub has six, and drawing
                  five would show six-of-six as "five filled" — i.e. as no change at all. */}
              {Array.from({ length: Math.max(l.sids.length, 5) }, (_, n) => (
                <span
                  key={n}
                  className="inline-block h-2 w-2"
                  style={{ borderRadius: '1px', background: n < l.overlap ? 'var(--accent)' : 'var(--border)' }}
                />
              ))}
            </span>
            <span className="truncate text-text-faint" title={l.names.join(', ')}>
              {i === 0
                ? ours
                  ? 'our current lineup'
                  : 'their latest lineup'
                : `${l.overlap}/${l.sids.length} of it`}{' '}
              ·{' '}
              {l.names.join(', ')}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
